import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, MapPin, ExternalLink, AlertTriangle, CheckCircle, ShieldAlert } from 'lucide-react';
import { hotels, destinations } from '../data';
import { tourAreas, disclaimerText } from '../tourData';
import SEO from '../components/SEO';
import { useLanguage } from '../components/LanguageContext';
import { buildBreadcrumbSchema, buildOrganizationSchema } from '../lib/schema';

const TourSuggestionsPage = () => {
  const { hotelId, id: destinationId } = useParams();
  const { language, translateObject, getLocalizedPath } = useLanguage();

  let currentArea = null;
  let hotel = null;
  let destination = null;
  let backLink = getLocalizedPath('destinations', language);
  let backText = language === 'en' ? 'Destinations' : 'Destinationer';

  if (hotelId) {
    const rawHotel = hotels.find((h) => h.id === hotelId);
    if (rawHotel) {
      hotel = translateObject(rawHotel);
      const rawArea = tourAreas.find((area) => area.hotelIds.includes(hotelId));
      if (rawArea) {
        currentArea = translateObject(rawArea);
      }
      const rawDest = destinations.find((d) => d.id === hotel.destinationId);
      if (rawDest) {
        destination = translateObject(rawDest);
      }
      backLink = getLocalizedPath('hotel', language, { hotelId });
      backText = hotel.name;
    }
  } else if (destinationId) {
    const rawDest = destinations.find((d) => d.id === destinationId);
    if (rawDest) {
      destination = translateObject(rawDest);
      const rawArea = tourAreas.find((area) => area.destinationId === destinationId);
      if (rawArea) {
        currentArea = translateObject(rawArea);
      }
      backLink = getLocalizedPath('destination', language, { id: destinationId });
      backText = destination.title;
    }
  }

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [hotelId, destinationId]);

  if (!currentArea) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 pt-24">
        <div className="text-center p-6 bg-white rounded-3xl shadow-md max-w-md">
          <h2 className="text-2xl font-serif font-bold text-emerald-900 mb-4">
            {language === 'en' ? 'No tour suggestions found' : 'Ingen turforslag fundet'}
          </h2>
          <p className="text-slate-600 mb-6">
            {language === 'en' ? 'We could not find any tour suggestions for this selection.' : 'Vi kunne ikke finde nogen turforslag for dette valg.'}
          </p>
          <Link to={getLocalizedPath('destinations', language)} className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-900 text-white font-bold rounded-xl hover:bg-emerald-800 transition-all">
            <ArrowLeft size={16} />
            {language === 'en' ? 'Go to destinations' : 'Gå til destinationer'}
          </Link>
        </div>
      </div>
    );
  }

  const disclaimerTextEn = 'Note: These tour suggestions are for inspiration only. Alora Rejser is not responsible for any changes in opening hours, prices, availability, or unforeseen changes with external providers. We always recommend confirming accessibility conditions directly with the provider or Get Your Guide before booking.';
  const activeDisclaimer = language === 'en' ? disclaimerTextEn : disclaimerText;

  const pageTitle = currentArea 
    ? (language === 'en' ? `Tour Suggestions: ${currentArea.title}` : `Turforslag: ${currentArea.title}`)
    : (language === 'en' ? "Local Experiences & Tour Suggestions" : "Lokale Oplevelser & Turforslag");

  const pageDescription = hotel 
    ? (language === 'en' ? `See exciting and accessible tour suggestions and local experiences near ${hotel.name}.` : `Se spændende og tilgængelige turforslag og lokale oplevelser i nærheden af ${hotel.name}.`)
    : (language === 'en' ? `See accessible tour suggestions and local experiences for the destination ${destination?.title || ''}.` : `Se tilgængelige turforslag og lokale oplevelser for destinationen ${destination?.title || ''}.`);

  const currentCanonicalUrl = hotelId 
    ? getLocalizedPath('hotel-tour', language, { hotelId })
    : getLocalizedPath('destination-tour', language, { id: destinationId });

  const breadcrumbs = [
    {
      name: language === 'en' ? 'Home' : 'Forside',
      url: getLocalizedPath('home', language)
    },
    ...(hotel ? [
      ...(destination ? [{
        name: destination.title,
        url: getLocalizedPath('destination', language, { id: hotel.destinationId })
      }] : []),
      {
        name: hotel.name,
        url: getLocalizedPath('hotel', language, { hotelId: hotel.id })
      }
    ] : destination ? [
      {
        name: language === 'en' ? 'Destinations' : 'Destinationer',
        url: getLocalizedPath('destinations', language)
      },
      {
        name: destination.title,
        url: getLocalizedPath('destination', language, { id: destination.id })
      }
    ] : []),
    {
      name: language === 'en' ? 'Tour suggestions' : 'Turforslag',
      url: currentCanonicalUrl
    }
  ];

  const breadcrumbSchema = buildBreadcrumbSchema(breadcrumbs);
  const organizationSchema = buildOrganizationSchema({ language });

  return (
    <div className="bg-stone-50 min-h-screen pb-20 pt-24 md:pt-28">
      <SEO 
        title={pageTitle} 
        description={pageDescription}
        ogImage={currentArea?.image}
        schemaMarkup={[organizationSchema, breadcrumbSchema]}
      />
      {/* Hero Section */}
      <div className="relative h-[45vh] overflow-hidden">
        <img 
          src={currentArea.image} 
          alt={currentArea.title} 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/50" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white px-4 max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-block px-4 py-1 bg-emerald-600/80 backdrop-blur-sm text-white rounded-full text-xs font-bold uppercase tracking-widest mb-4">
                {language === 'en' ? 'Local Experiences & Tour Suggestions' : 'Lokale Oplevelser & Turforslag'}
              </span>
              <h1 className="text-3xl md:text-6xl font-serif font-bold mb-4 drop-shadow-lg italic">
                {currentArea.title}
              </h1>
              <p className="text-base md:text-xl italic font-light drop-shadow-md max-w-2xl mx-auto text-stone-100">
                {hotel 
                  ? (language === 'en' ? `Handpicked tour suggestions near ${hotel.name}` : `Håndplukkede turforslag i nærheden af ${hotel.name}`) 
                  : (language === 'en' ? `Experiences and tour suggestions for the destination ${destination?.title}` : `Oplevelser og turforslag for destinationen ${destination?.title}`)}
              </p>
            </motion.div>
          </div>
        </div>
        
        <Link 
          to={backLink} 
          className="absolute top-8 left-8 bg-white/90 backdrop-blur-sm text-emerald-900 p-3 rounded-full hover:bg-white transition-colors shadow-lg z-10 flex items-center gap-2 text-sm font-bold pr-5"
        >
          <ArrowLeft size={18} />
          <span>{language === 'en' ? 'Back to' : 'Tilbage til'} {backText}</span>
        </Link>
      </div>

      {/* Content Section */}
      <div className="max-w-7xl mx-auto px-4 mt-12 relative z-20">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          
          {/* Tour Suggestions List */}
          <div className="lg:col-span-2 space-y-8">
            <div className="bg-white rounded-[2rem] p-8 md:p-10 shadow-sm border border-emerald-50">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 pb-4 border-b border-stone-100 gap-4">
                <div>
                  <h2 className="text-2xl md:text-3xl font-serif font-bold text-emerald-900">
                    {language === 'en' ? 'Our tour suggestions' : 'Vores turforslag'}
                  </h2>
                  <p className="text-sm text-slate-500 mt-1">
                    {language === 'en' ? 'Carefully selected experiences with a focus on accessibility' : 'Nøje udvalgte oplevelser med fokus på tilgængelighed'}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <a 
                    href={currentArea.getYourGuideUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-800 hover:text-emerald-900 bg-emerald-50 hover:bg-emerald-100 px-3.5 py-2 rounded-xl transition-all border border-emerald-100 shadow-sm"
                  >
                    <span>{language === 'en' ? 'Search on Get Your Guide' : 'Søg på Get Your Guide'}</span>
                    <ExternalLink size={14} />
                  </a>
                  <span className="text-3xl hidden sm:inline">🗺️</span>
                </div>
              </div>

              {/* Meget tydelig Get Your Guide banner / link */}
              <div className="mb-8 p-5 bg-emerald-50/60 rounded-2xl border border-emerald-100/80 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-start gap-3">
                  <span className="text-2xl mt-0.5">✨</span>
                  <div>
                    <h4 className="font-bold text-emerald-900 text-sm">
                      {language === 'en' ? 'Want to see even more accessible tours?' : 'Vil du se endnu flere tilgængelige ture?'}
                    </h4>
                    <p className="text-xs text-slate-600 mt-0.5 leading-relaxed">
                      {language === 'en' ? (
                        <>
                          We have prepared a search on <strong className="font-bold text-emerald-800">Get Your Guide</strong> where the filter for wheelchair-friendly experiences is automatically enabled.
                        </>
                      ) : (
                        <>
                          Vi har forberedt en søgning på <strong className="font-bold text-emerald-800">Get Your Guide</strong>, hvor filteret for kørestolsvenlige oplevelser automatisk er slået til.
                        </>
                      )}
                    </p>
                  </div>
                </div>
                <a 
                  href={currentArea.getYourGuideUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-emerald-800 hover:bg-emerald-900 text-white text-xs font-bold rounded-xl transition-all shrink-0 shadow-sm hover:shadow-md transform hover:-translate-y-0.5"
                >
                  <span>{language === 'en' ? 'Search on Get Your Guide' : 'Søg på Get Your Guide'}</span>
                  <ExternalLink size={14} />
                </a>
              </div>

              <div className="space-y-6">
                {currentArea.tours.map((tour, idx) => (
                  <div 
                    key={idx} 
                    className={`p-6 rounded-2xl border transition-all ${
                      tour.isNotAccessible 
                        ? 'bg-red-50/40 border-red-100 hover:bg-red-50/60' 
                        : 'bg-stone-50/50 border-stone-100 hover:bg-stone-50 hover:border-emerald-100'
                    }`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
                          tour.isNotAccessible ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-800'
                        }`}>
                          {tour.isNotAccessible ? <AlertTriangle size={20} /> : <MapPin size={20} />}
                        </div>
                        <div>
                          <h4 className="font-bold text-lg text-slate-900 flex flex-wrap items-center gap-2">
                            {tour.title}
                            {tour.isNotAccessible && (
                              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-800">
                                {language === 'en' ? 'Not wheelchair accessible' : 'Ikke kørestolsegnet'}
                              </span>
                            )}
                          </h4>
                          <p className="text-slate-600 text-sm leading-relaxed mt-2">{tour.description}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar with Get Your Guide & Disclaimer */}
          <div className="lg:col-span-1 space-y-8">
            
            {/* Get Your Guide Integration Card */}
            <div className="bg-gradient-to-br from-emerald-900 to-emerald-950 text-white rounded-[2rem] p-8 shadow-xl relative overflow-hidden">
              <div className="absolute -right-10 -bottom-10 w-40 h-40 bg-white/5 rounded-full pointer-events-none" />
              
              <div className="flex items-center gap-3 mb-6">
                <span className="text-3xl">✨</span>
                <h3 className="text-2xl font-serif font-bold italic">Get Your Guide</h3>
              </div>

              <p className="text-emerald-100 text-sm leading-relaxed mb-6">
                {language === 'en' ? (
                  <>
                    Want to explore even more options? We have created a direct link to <strong>Get Your Guide</strong>, where the search is pre-set with the <strong>wheelchair icon enabled</strong> in their search field.
                  </>
                ) : (
                  <>
                    Vil du udforske endnu flere muligheder? Vi har lavet et direkte link til <strong>Get Your Guide</strong>, hvor søgningen er forudindstillet med <strong>kørestols-ikonet slået til</strong> i deres søgefelt.
                  </>
                )}
              </p>

              <div className="bg-white/10 p-4 rounded-xl border border-white/10 mb-8">
                <div className="flex items-start gap-3">
                  <CheckCircle size={18} className="text-emerald-300 shrink-0 mt-0.5" />
                  <p className="text-xs text-emerald-100 leading-relaxed">
                    {language === 'en' ? (
                      'The link automatically filters out unsuitable activities, so you are only presented with experiences with documented accessibility.'
                    ) : (
                      'Linket filtrerer automatisk uegnede aktiviteter fra, så du kun præsenteres for oplevelser med dokumenteret tilgængelighed.'
                    )}
                  </p>
                </div>
              </div>

              <a 
                href={currentArea.getYourGuideUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-4 bg-[#99A18C] hover:bg-[#8a917e] text-white text-center font-bold rounded-xl transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                <span>{language === 'en' ? 'Find more tours on Get Your Guide' : 'Find flere ture på Get Your Guide'}</span>
                <ExternalLink size={16} />
              </a>
            </div>

            {/* Disclaimer Card */}
            <div className="bg-amber-50/60 border border-amber-200/60 rounded-2xl p-6 shadow-sm">
              <div className="flex items-start gap-3">
                <ShieldAlert className="text-amber-700 shrink-0 mt-0.5" size={20} />
                <div>
                  <h4 className="font-bold text-amber-900 text-sm mb-2">
                    {language === 'en' ? 'Important information about tour suggestions' : 'Vigtig information om turforslag'}
                  </h4>
                  <p className="text-xs text-amber-800 leading-relaxed">
                    {activeDisclaimer}
                  </p>
                </div>
              </div>
            </div>

          </div>

        </div>
      </div>
    </div>
  );
};

export default TourSuggestionsPage;
